import validators from './validation'

// Allows optional validators to reuse prop values from their base validators
const validatorPropAliases = {
  optionalMin: 'min',
}

/**
 * Memoizes a function.
 * @template T
 * @param {function(...*): T} fn - The function to memoize.
 * @param {function(...*): string} getKey - Function to generate a cache key.
 * @returns {function(...*): T} Memoized function.
 */
const memoize = (fn, getKey) => {
  const cache = new Map()
  return (...args) => {
    const key = getKey ? getKey(...args) : args[0]
    if (cache.has(key)) return cache.get(key)
    const result = fn(...args)
    cache.set(key, result)
    return result
  }
}

/**
 * Creates a validator function based on the given parameters.
 * @param {Object} validators - Object containing validator functions.
 * @param {string} validator - Name of the validator to create.
 * @param {*} options - Options for the validator.
 * @returns {function|undefined} Created validator function or undefined.
 */
const createValidator = (validators, validator, options) =>
  validators[validator] ? validators[validator](options) : undefined

const memoizedCreateValidator = memoize(createValidator, (...args) =>
  JSON.stringify(args),
)

/**
 * Creates an array of validator functions from props.
 * @param {Object} props - Props object containing validator information.
 * @returns {Array<function>} Array of validator functions.
 */
export const createValidatorsFromProps = props => {
  const validatorNames = (props.validator || '').split(',')

  return validatorNames
    .map(name => name.trim())
    .filter(Boolean)
    .map(name => {
      const propName = validatorPropAliases[name] || name
      return memoizedCreateValidator(
        validators,
        name,
        props[`validator-${propName}`],
      )
    })
    .filter(Boolean)
}

/**
 * Safely gets a nested property from an object.
 * @param {Object} obj - The object to retrieve the property from.
 * @param {string} path - The path to the property, using dot notation.
 * @returns {*} The value at the specified path, or undefined if not found.
 */
const getNestedProperty = (obj, path) =>
  path.split('.').reduce((acc, key) => acc && acc[key], obj)

/**
 * Adds custom validators to form values.
 * @param {Object} values - The current form values.
 * @param {Array<Object>} validators - Array of validator objects.
 * @returns {Object} An object containing any validation errors.
 */
export const addCustomValidators = (values, validators = []) =>
  validators.reduce((errors, { name = '', errorMessage, isFieldInvalid }) => {
    const fieldValue = getNestedProperty(values, name)
    return isFieldInvalid(fieldValue)
      ? { ...errors, [name]: errorMessage }
      : errors
  }, {})
